import { useMemo, useState } from "react";
import { Stack } from "@mui/material";

import ChartBlock, {
  chartHasSeriesData,
  pricingLineChartOptions,
} from "../../../routes/pricing-reports/ChartBlock";
import DesignAccordion from "../../../routes/pricing-reports/design/Accordion";
import { CHART_COLORS } from "../../../routes/pricing-reports/pricingChartsUtils";

export const STREET_TEAM_CHART_LIMIT = 12;

const chartFrameSx = { p: 3.5 };

const streetTeamBarOptions = {
  ...pricingLineChartOptions,
  indexAxis: "y",
  layout: {
    padding: { top: 8, right: 16, bottom: 8, left: 4 },
  },
  plugins: {
    ...pricingLineChartOptions.plugins,
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = typeof context.parsed?.x === "number" ? context.parsed.x : 0;
          return `Images: ${value.toLocaleString()}`;
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      title: { display: true, text: "Images" },
    },
    y: {
      ticks: { autoSkip: false },
    },
  },
};

export function buildStreetTeamBarChart(rows = [], { limit = STREET_TEAM_CHART_LIMIT } = {}) {
  const limited = (rows || []).slice(0, limit);
  return {
    labels: limited.map((row) => row.label),
    datasets: [
      {
        label: "Images",
        data: limited.map((row) => Number(row.count) || 0),
        backgroundColor: limited.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
      },
    ],
  };
}

export function StreetTeamInlineChart({ title, subtitle, rows = [], isLoading = false, error = null }) {
  const [expanded, setExpanded] = useState(false);
  const chart = useMemo(
    () => buildStreetTeamBarChart(rows, { limit: Math.max(rows.length, 1) }),
    [rows]
  );

  return (
    <DesignAccordion
      title={title}
      subtitle={subtitle}
      expanded={expanded}
      onChange={setExpanded}
    >
      <ChartBlock
        chartType="bar"
        chart={chart}
        chartOptions={streetTeamBarOptions}
        hasData={chartHasSeriesData(chart)}
        isLoading={isLoading}
        error={error}
        emptyMessage="No retailer counts for this province."
        sx={chartFrameSx}
      />
    </DesignAccordion>
  );
}

export default function StreetTeamChart({ title, subtitle, chart, isLoading, error }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <DesignAccordion
      title="Image counts"
      subtitle="- Top groups for the selected date range."
      expanded={expanded}
      onChange={setExpanded}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <ChartBlock
          title={title}
          subtitle={subtitle}
          chartType="bar"
          chart={chart}
          chartOptions={streetTeamBarOptions}
          hasData={chartHasSeriesData(chart)}
          isLoading={isLoading}
          error={error}
          emptyMessage="No image counts for the current date range."
          sx={chartFrameSx}
        />
      </Stack>
    </DesignAccordion>
  );
}
